import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../../types';
import {type SemanticLegendPalette} from './colorPalettes';
import {
  normalizeNumericSemanticValue,
  resolveSemanticLegendAssignment,
  type SemanticLegendAssignment,
  type SemanticLegendRuntimeValue,
} from './resolveSemanticLegendAssignment';
import {type ConfigValueForRun} from './useConfigValuesAggregation';

export type SemanticLegendAssignmentConfig = Pick<
  SemanticLegendSettingsWithCategoricalConfig,
  | 'key'
  | 'buckets'
  | 'bucketCustomizations'
  | 'excludedRunColor'
  | 'selectedCategories'
  | 'yAxisValue'
>;

export type SemanticLegendAssignmentRuntime = {
  valuesByRunName?: Partial<Record<string, SemanticLegendRuntimeValue>>;
};

export type SemanticLegendAssignmentSource = Pick<
  SemanticLegendSettingsWithCategoricalConfig,
  | 'configValuesForRun'
  | 'configValuesForRunCategories'
  | 'metricStatisticsForRun'
  | 'yAxisValue'
>;

export function getCategoryIndexByValue(
  settings: SemanticLegendAssignmentConfig
): Map<string, number> | undefined {
  if (settings.yAxisValue !== YAxisValues.Config_Categories) {
    return undefined;
  }
  return new Map(
    (settings.selectedCategories ?? []).map((category, index) => [
      category,
      index,
    ])
  );
}

export function getSemanticValueForRun(
  settings: SemanticLegendAssignmentSource,
  runName: string
): SemanticLegendRuntimeValue | undefined {
  if (settings.yAxisValue === YAxisValues.Config_Categories) {
    const configVal = settings.configValuesForRunCategories?.find(
      entry => entry.runName === runName
    );
    return configVal?.value == null ? undefined : String(configVal.value);
  }

  if (settings.yAxisValue === YAxisValues.Config_Number) {
    const configVal = settings.configValuesForRun?.find(
      entry => entry.runName === runName
    );
    return normalizeNumericSemanticValue(configVal?.value);
  }

  const metricStat = settings.metricStatisticsForRun?.find(
    entry => entry.run_name === runName
  );
  if (metricStat != null && isMetricStatisticYAxisValue(settings.yAxisValue)) {
    return normalizeNumericSemanticValue(metricStat[settings.yAxisValue]);
  }

  return undefined;
}

function isMetricStatisticYAxisValue(
  yAxisValue: YAxisValues | undefined
): yAxisValue is YAxisValues.Min | YAxisValues.Max | YAxisValues.Last {
  return (
    yAxisValue === YAxisValues.Min ||
    yAxisValue === YAxisValues.Max ||
    yAxisValue === YAxisValues.Last
  );
}

type BuildSemanticLegendAssignmentsByRunNameArgs = {
  categoryIndexByValue?: Map<string, number>;
  configValuesForRunCategories?: ConfigValueForRun[];
  runtime?: SemanticLegendAssignmentRuntime;
  semanticLegendPalette?: SemanticLegendPalette;
  settings: SemanticLegendAssignmentConfig;
};

export function buildSemanticLegendAssignmentsByRunName({
  categoryIndexByValue,
  configValuesForRunCategories,
  runtime,
  semanticLegendPalette,
  settings,
}: BuildSemanticLegendAssignmentsByRunNameArgs): Map<
  string,
  SemanticLegendAssignment
> {
  const assignments = new Map<string, SemanticLegendAssignment>();
  const categoryIndex =
    categoryIndexByValue ?? getCategoryIndexByValue(settings);

  if (runtime?.valuesByRunName != null) {
    for (const [runName, value] of Object.entries(runtime.valuesByRunName)) {
      assignments.set(
        runName,
        resolveSemanticLegendAssignment({
          categoryIndexByValue: categoryIndex,
          semanticLegendPalette,
          settings,
          value,
        })
      );
    }
    return assignments;
  }

  if (settings.yAxisValue === YAxisValues.Config_Categories) {
    for (const entry of configValuesForRunCategories ?? []) {
      assignments.set(
        entry.runName,
        resolveSemanticLegendAssignment({
          categoryIndexByValue: categoryIndex,
          semanticLegendPalette,
          settings,
          value: entry.value == null ? undefined : String(entry.value),
        })
      );
    }
  }

  return assignments;
}

export function assignmentsToColorMap(
  assignments: Map<string, SemanticLegendAssignment>
): Partial<Record<string, string>> {
  return Object.fromEntries(
    [...assignments.entries()].map(([runName, assignment]) => [
      runName,
      assignment.color,
    ])
  );
}

export function resolveSemanticLegendAssignmentForRun({
  runName,
  semanticLegendPalette,
  settings,
}: {
  runName: string;
  semanticLegendPalette?: SemanticLegendPalette;
  settings: SemanticLegendAssignmentConfig & SemanticLegendAssignmentSource;
}): SemanticLegendAssignment {
  return resolveSemanticLegendAssignment({
    categoryIndexByValue: getCategoryIndexByValue(settings),
    semanticLegendPalette,
    settings,
    value: getSemanticValueForRun(settings, runName),
  });
}
